import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { interval, Subscription, take, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.scss'],
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  title: string = '';
  message: string = '';
  delete?: boolean = false;
  timer?: boolean = false;
  italicMessage?: string = '';
  bullets?: string[] = [];
  hideActionBtn?: boolean = false;
  actionButtonText?: string = '';
  cancelButtonText?: string = '';
  buttonsAlign?: string = '';
  middleButtonText?: string = '';
  middleTextBG?: string = '';
  disableMainAction?: boolean = false;
  givenIcon?: string = '';
  cancelBtnValue?: string = '';
  okButtonText: string = '';

  countdown: number = 3;
  isYesButtonDisabled: boolean = true;
  countdownSubscription: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogModel,
    private translocoService: TranslocoService,
  ) {
    // Update view with given values
    this.title = data.title;
    this.message = data.message;
    this.delete = data.typeDelete;
    this.timer = data.timer;
    this.italicMessage = data.italicMessage;
    this.bullets = data.bullets;
    this.actionButtonText = data.actionButtonText;
    this.cancelButtonText = data.cancelButtonText;
    this.buttonsAlign = data.buttonsAlign;
    this.hideActionBtn = data.hideActionBtn;
    this.middleButtonText = data.middleButtonText;
    this.middleTextBG = data.middleTextBG;
    this.disableMainAction = data.disableMainAction;
    this.givenIcon = data.givenIcon;
    this.cancelBtnValue = data.cancelBtnValue;

    // Translate OK button text
    this.okButtonText = this.translocoService.translate('Ok');
  }

  ngOnInit() {
    // Update OK button text on language changes
    this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.okButtonText = this.translocoService.translate('Ok');
    });

    if (this.timer) {
      this.startCountdown();
    } else {
      this.isYesButtonDisabled = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirm(): void {
    // Close the dialog, return true
    this.dialogRef.close(true);
  }

  onMiddle(): void {
    //Close and return 'middle button text'
    this.dialogRef.close('middleOption');
  }

  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(this.cancelBtnValue ?? false);
  }

  startCountdown() {
    this.countdownSubscription = interval(1000)
      .pipe(take(3))
      .subscribe({
        next: (val) => (this.countdown -= 1),
        complete: () => this.enableYesButton(),
      });
  }

  enableYesButton() {
    this.countdownSubscription.unsubscribe();
    this.isYesButtonDisabled = false;
  }
}

/**
 * Class to represent confirm dialog model.
 *
 * It has been kept here to keep it as part of shared component.
 */
export class ConfirmDialogModel {
  constructor(
    public title: string,
    public message?: string,
    public typeDelete?: boolean,
    public timer?: boolean,
    public italicMessage?: string,
    public actionButtonText?: string,
    public cancelButtonText?: string,
    public buttonsAlign?: string,
    public hideActionBtn?: boolean,
    public bullets?: string[],
    public middleButtonText?: string,
    public disableMainAction?: boolean,
    public givenIcon?: string,
    public middleTextBG?: string,
    public cancelBtnValue?: string,
  ) {}
}
